import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Globe, AtSign, Loader2, Trophy, ShieldCheck, ArrowUpRight, Info } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fetchConfig, fetchLeaderboard, createNewListingCheckout, joinWaitlist } from "@/lib/api";
import { money } from "@/lib/format";

const NONE = "__none__";

export default function Outbid() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const prefillBid = parseInt(params.get("bid") || "", 10);

    const config = useQuery({ queryKey: ["config"], queryFn: fetchConfig });
    const lb = useQuery({ queryKey: ["leaderboard"], queryFn: fetchLeaderboard });

    const minBid = config.data?.min_new_bid || 5;
    const specialties = config.data?.specialties || [];

    const [kind, setKind] = useState("url");
    const [value, setValue] = useState("");
    const [bid, setBid] = useState("");
    const [description, setDescription] = useState("");
    const [specialty, setSpecialty] = useState(NONE);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!Number.isNaN(prefillBid) && prefillBid >= minBid) setBid(String(prefillBid));
    }, [prefillBid, minBid]);

    const paidOn = !!config.data?.paid_slots_enabled;

    const bidNum = Number(bid);
    const bidValid = Number.isInteger(bidNum) && bidNum >= minBid;

    const projectedRank = useMemo(() => {
        if (!bidValid) return null;
        const rows = lb.data?.listings || [];
        // new listing ranks below everyone with an equal or higher bid (older-first tie-break)
        const above = rows.filter((r) => Number(r.bid_amount) >= bidNum).length;
        return above + 1;
    }, [bidValid, bidNum, lb.data]);

    const valueValid = value.trim().length > 0;
    const canSubmit = valueValid && bidValid && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            const res = await createNewListingCheckout({
                type: kind,
                value: value.trim(),
                bid_amount: bidNum,
                description: description.trim() || undefined,
                specialty: specialty === NONE ? "" : specialty,
                email: email.trim() || undefined,
            });
            window.location.assign(res.checkout_url);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not start checkout. Check your entry and try again.";
            toast.error(msg);
            setBusy(false);
        }
    };

    if (!config.isLoading && config.data && !paidOn) {
        return <PaidWaitlist onList={() => navigate("/list")} />;
    }

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
            <div className="mb-8">
                <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Claim a featured slot</h1>
                <p className="text-muted-foreground mt-2">Paid placement above the crowd board. Crowd #1 stays likes. You pay once and the slot goes live when payment clears.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {/* FORM */}
                <Card className="md:col-span-7 bg-card border-border p-5 sm:p-6">
                    <div className="space-y-5">
                        {/* URL / handle toggle */}
                        <div className="space-y-2">
                            <Label>What are you listing?</Label>
                            <Tabs value={kind} onValueChange={(v) => { setKind(v); setValue(""); }}>
                                <TabsList className="grid grid-cols-2 w-full">
                                    <TabsTrigger value="url" data-testid="outbid-tab-url"><Globe className="h-4 w-4 mr-1.5" /> App Link / Website</TabsTrigger>
                                    <TabsTrigger value="handle" data-testid="outbid-tab-handle"><AtSign className="h-4 w-4 mr-1.5" /> X @handle</TabsTrigger>
                                </TabsList>
                            </Tabs>
                            <div className="relative">
                                {kind === "handle" && (
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
                                )}
                                <Input
                                    data-testid="outbid-value-input"
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    placeholder={kind === "url" ? "yourfitnessapp.com (or App Store URL)" : "yourapphandle"}
                                    className={kind === "handle" ? "pl-7" : ""}
                                />
                            </div>
                        </div>

                        {/* Bid */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="outbid-bid">Your bid (USD, whole dollars)</Label>
                                {projectedRank ? (
                                    <span data-testid="outbid-rank-preview" className="inline-flex items-center gap-1 rounded-full bg-brand/15 text-brand text-xs font-semibold px-2.5 py-1">
                                        <Trophy className="h-3 w-3" /> Ranks #{projectedRank}
                                    </span>
                                ) : null}
                            </div>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                                <Input
                                    id="outbid-bid"
                                    data-testid="outbid-bid-input"
                                    type="number"
                                    min={minBid}
                                    step="1"
                                    value={bid}
                                    onChange={(e) => setBid(e.target.value)}
                                    placeholder={String(minBid)}
                                    className="pl-7 tabular text-lg"
                                />
                            </div>
                            <p className={`text-xs ${bid && !bidValid ? "text-heat" : "text-muted-foreground"}`}>
                                {bid && !bidValid ? `Minimum bid is ${money(minBid)} (whole dollars only).` : `Minimum ${money(minBid)}. You pay this once and go live instantly.`}
                            </p>
                        </div>

                        {/* Specialty */}
                        <div className="space-y-2">
                            <Label>Specialty <span className="text-muted-foreground font-normal">(optional)</span></Label>
                            <Select value={specialty} onValueChange={setSpecialty}>
                                <SelectTrigger data-testid="outbid-specialty-select">
                                    <SelectValue placeholder="e.g. Workout Tracker, AI Workout, Macro & Meal Planner" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={NONE}>No specialty</SelectItem>
                                    {specialties.map((s) => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                            <Label htmlFor="outbid-desc">Short pitch <span className="text-muted-foreground font-normal">(optional)</span></Label>
                            <Textarea
                                id="outbid-desc"
                                data-testid="outbid-description-input"
                                maxLength={200}
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="AI workout tracker with real-time Apple Watch sync and smart progressive overload."
                            />
                            <p className="text-xs text-muted-foreground text-right">{description.length}/200</p>
                        </div>

                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="outbid-email">Email <span className="text-muted-foreground font-normal">(optional receipt)</span></Label>
                            <Input
                                id="outbid-email"
                                data-testid="outbid-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                            />
                            <p className="text-xs text-muted-foreground">Receipt only. No account created.</p>
                        </div>

                        <Button
                            data-testid="outbid-submit-button"
                            disabled={!canSubmit}
                            onClick={submit}
                            className="w-full bg-brand text-primary-foreground hover:bg-brand-dim font-semibold text-base h-11"
                        >
                            {busy ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Opening checkout…</>
                            ) : (
                                <>Continue to payment {bidValid ? `· ${money(bidNum)}` : ""} <ArrowUpRight className="h-4 w-4 ml-1" /></>
                            )}
                        </Button>
                    </div>
                </Card>

                {/* PREVIEW / RULES */}
                <div className="md:col-span-5 space-y-4">
                    <Card className="bg-card border-border p-5">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Your projected spot</h3>
                        <div className="rounded-xl border border-brand/30 bg-brand/5 p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-heading text-2xl font-bold">
                                        {projectedRank ? `#${projectedRank}` : "#—"}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">projected rank</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-heading text-2xl font-bold tabular text-brand">{bidValid ? money(bidNum) : "$—"}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">your bid</div>
                                </div>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-3 flex gap-1.5">
                            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            Paying less than #1 still lists you — at the highest rank your bid can buy. Equal bids? The earlier listing stays higher.
                        </p>
                    </Card>

                    <Card className="bg-card border-border p-5">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Quick rules</h3>
                        <ul className="space-y-2 text-sm">
                            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Fitness apps, trackers, digital products &amp; coaches only.</li>
                            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Real App Store, Play Store, website or X handle — no shorteners.</li>
                            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-brand shrink-0 mt-0.5" /> No chat/invite links, tracking params or NSFW.</li>
                        </ul>
                        <Button variant="link" onClick={() => navigate("/rules")} className="px-0 mt-2 text-brand" data-testid="outbid-see-rules-link">
                            See full rules →
                        </Button>
                    </Card>
                </div>
            </div>
        </div>
    );
}

function PaidWaitlist({ onList }) {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async () => {
        if (!email.trim() || busy) return;
        setBusy(true);
        try {
            await joinWaitlist(email.trim());
            setDone(true);
            toast.success("We will email you when featured slots open.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save that email.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-16">
            <Card className="bg-card border-border p-8" data-testid="paid-waitlist">
                <h1 className="font-heading text-2xl font-bold">Featured slots are not open yet</h1>
                <p className="text-muted-foreground mt-3">
                    Rank on the crowd board is likes, not dollars. Paid featured placement opens after the board has heat. Leave an email if you want the first shot.
                </p>
                <div className="mt-6 space-y-3">
                    <Label htmlFor="waitlist-email">Email</Label>
                    <Input
                        id="waitlist-email"
                        data-testid="waitlist-email-input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        disabled={done}
                    />
                    <Button
                        data-testid="waitlist-submit-button"
                        onClick={submit}
                        disabled={done || busy || !email.trim()}
                        className="w-full bg-brand text-primary-foreground hover:bg-brand-dim font-semibold"
                    >
                        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        {done ? "You're on the list" : "Notify me when slots open"}
                    </Button>
                    <Button
                        data-testid="waitlist-list-button"
                        variant="outline"
                        onClick={onList}
                        className="w-full font-semibold"
                    >
                        List free with a tweet
                    </Button>
                </div>
            </Card>
        </div>
    );
}
